/**
 * Grilla unificada de consumos del proveedor (spec 006, MedicalProtocols spec 028). TODOS los DTO de
 * los endpoints 028 que usa la pantalla viven acá, para alinearlos en un solo lugar si el backend
 * ajusta las formas JSON. Los enums viajan como números.
 */
import { ImportAssignmentStatus, ImportedProviderConsumption } from '../ProviderConsumptionImport/ProviderConsumptionImport';

/** Origen del consumo de proveedor (ConsumptionOriginEnum de MP). */
export enum ProviderConsumptionOrigin {
  SyncProcess = 1,
  Manual = 2,
  FileImport = 3,
  ExternalInformed = 4,
  ProviderFileImport = 5,
}

export const PROVIDER_CONSUMPTION_ORIGINS: ProviderConsumptionOrigin[] = [
  ProviderConsumptionOrigin.SyncProcess,
  ProviderConsumptionOrigin.Manual,
  ProviderConsumptionOrigin.FileImport,
  ProviderConsumptionOrigin.ExternalInformed,
  ProviderConsumptionOrigin.ProviderFileImport,
];

/** Estado del consumo (ProtocolConsumedServiceStatus). */
export const PROVIDER_CONSUMPTION_STATUSES = [0, 1, 2, 3, 4, 5, 6, 7, 8];

/** De dónde sale el costo (costValuationSource). `null` = sin valorizar. */
export enum CostValuationSource {
  FixedConceptCost = 1,
  ProviderPriceList = 2,
  ConceptPercentageCost = 3,
  /** Valor del archivo importado (origen 5, MP 030). */
  ImportedValue = 4,
}

/** Tipo de precio aplicado de la lista (appliedPriceType). */
export const APPLIED_PRICE_TYPES = [1, 2, 3, 4, 5];

/** Motivos de alarma (settlementAlarmReason / reason). 11 sólo aparece en alarmas de corrida. */
export const SETTLEMENT_ALARM_REASONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

/** Estado de una corrida de sincronización. */
export enum ProviderSyncStatus {
  Registered = 1,
  InProgress = 2,
  Finished = 3,
  Cancelled = 4,
  Fail = 5,
  PartiallyFailed = 6,
}

/** Qué disparó la corrida. */
export enum ProviderSyncTrigger {
  OnDemand = 1,
  ProtocolSyncChained = 2,
  ProtocolConsumptionChanged = 3,
}

/** Ítem de la grilla (ProviderConsumptionViewModel 028); incluye los campos de importación de la spec 005. */
export interface ProviderConsumption extends ImportedProviderConsumption {
  patientMatchOrigin?: number | null;
  addendumId?: number | null;
  stageId?: number | null;
  stageName?: string | null;
  quantity?: number | null;
  unitAmount?: number | null;
  pendingAmount?: number | null;
  payeeAmount?: number | null;
  sourceProtocolConsumptionId?: string | null;
  settlementPath?: number | null;
  costValuationSource?: number | null;
  priceListId?: number | null;
  providerPriceListAssociationId?: number | null;
  priceListEffectiveDateStart?: string | null;
  priceListEffectiveDateEnd?: string | null;
  appliedPriceType?: number | null;
  settlementAlarmReason?: number | null;
  settlementAlarmDetail?: string | null;
  settlementAlarmReasonDescription?: string | null;
  hasAlarm?: boolean | null;
  costPercentage?: number | null;
  costBaseUnitAmount?: number | null;
  /** Texto ("Unassigned" | "Assigned" | "Generated") sólo en origen 5; null en el resto. */
  assignmentStatus?: ImportAssignmentStatus | null;
  /** Liquidación a la que fue el consumo (MP la completa con OnCreateSettlement; spec 010). */
  settlementId?: number | null;
  settlementIdentifier?: string | null;
  settlementName?: string | null;
  /** Moneda, importes y cotización originales (ProviderConsumptionViewModel); el detalle los muestra sólo si hay conversión (spec 010, US8). */
  originalCurrencyCode?: string | null;
  originalUnitAmount?: number | null;
  originalAmount?: number | null;
  rate?: number | null;
}

/** Estados que usa la liquidación de proveedor (ProtocolConsumedServiceStatus), sin números mágicos. */
export enum ProviderConsumptionStatus {
  NotSettled = 0,
  Payee = 2,
  Settled = 3,
  ToSettle = 6,
}

/**
 * Filtro de PATCH providers/consumptions/to-settle por filtro (MP 032, `ProviderConsumptionParameters`): todos
 * los filtros activos de la grilla, sin paginado ni orden. Enums como números; `hasAlarm` sólo viaja como `true`
 * (en MP `false` significa "sin alarma", y en la grilla es "sin filtro").
 */
export interface ProviderToSettleFilter {
  searchText?: string;
  origins?: number[];
  protocolId?: number;
  status?: number;
  hasAlarm?: true;
  dateFrom?: string;
  dateTo?: string;
  providerFileId?: string;
  /** Unassigned 1, Assigned 2, Generated 3. */
  assignmentStatus?: number;
}

/** Cuerpo de PATCH providers/consumptions/to-settle (MP 032): una de dos formas. */
export type ProviderToSettleRequest =
  | { ids: string[]; toSettle: boolean }
  | { providerId: number; filter: ProviderToSettleFilter; toSettle: boolean };

export type ProviderToSettleRejectReason = 'NotFound' | 'NotPending' | 'NotToSettle' | 'ZeroAmount' | 'HasAlarm' | 'WithoutProvider';

export interface ProviderToSettleRejection {
  id: string;
  /** Tolera valores nuevos del servidor. */
  reason: ProviderToSettleRejectReason | string;
  /** Texto del servidor: se muestra como texto. */
  message: string;
}

/** `data` del 200 (parcial, no todo o nada); con lotes se suman `updated` / `rejectedTotal` y se concatenan `rejected`. */
export interface ProviderToSettleResult {
  updated: number;
  /** Hasta 200 por respuesta. */
  rejected: ProviderToSettleRejection[];
  rejectedTotal: number;
}

/** Ids por pedido del marcado por selección. */
export const PROVIDER_TO_SETTLE_BATCH_SIZE = 1000;
/** Máximo de coincidencias del marcado por filtro (más → 400). */
export const PROVIDER_TO_SETTLE_FILTER_MAX = 5000;
/** Máximo de rechazos detallados por respuesta. */
export const PROVIDER_TO_SETTLE_REJECTED_MAX = 200;

/** Columnas por las que ordena la API (`SortBy`); cualquier otra es 400. */
export const PROVIDER_CONSUMPTION_SORT_KEYS = [
  'serviceDueDate',
  'amount',
  'unitAmount',
  'serviceName',
  'medicalServiceCode',
  'protocolId',
  'protocolName',
  'patientNumberInProtocol',
  'status',
  'origin',
  'currencyCode',
  'conceptDescription',
  'settlementAlarmReason',
  'created',
] as const;

export type ProviderConsumptionSortKey = (typeof PROVIDER_CONSUMPTION_SORT_KEYS)[number];
export type SortDirection = 'asc' | 'desc';

/** Filtros, paginado y orden de la grilla. Lista, totales y exportación usan los mismos filtros. */
export interface ProviderConsumptionsFilter {
  searchText?: string;
  origins?: number[];
  protocolId?: number;
  status?: number;
  hasAlarm?: boolean;
  /** aaaa-mm-dd (inclusive). */
  dateFrom?: string;
  /** aaaa-mm-dd (inclusive). */
  dateTo?: string;
  /** Sólo con origen 5. */
  providerFileId?: string;
  /** Sólo con origen 5. */
  assignmentStatus?: ImportAssignmentStatus;
  pageNumber: number;
  pageSize: number;
  sortBy?: ProviderConsumptionSortKey;
  sortDirection?: SortDirection;
}

export interface ProviderConsumptionsPage {
  items: ProviderConsumption[];
  totalRecords: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
}

export interface ProviderConsumptionCurrencyTotal {
  currencyCode: string;
  count: number;
  amount: number;
  alarmCount: number;
}

/** GET providers/{int}/consumptions/totals. */
export interface ProviderConsumptionTotals {
  totalCount: number;
  alarmCount: number;
  byCurrency: ProviderConsumptionCurrencyTotal[];
}

/** Corrida de sincronización (ProviderConsumptionSyncProcessViewModel). */
export interface ProviderSyncRun {
  id: string;
  providerId?: string | null;
  providerExternalId?: number | null;
  providerName?: string | null;
  /** null = alcance general (todos los protocolos, ventana de 6 meses). */
  protocolId?: number | null;
  windowStartDate?: string | null;
  trigger?: number | null;
  triggerProtocolId?: number | null;
  status: number;
  lastSync?: string | null;
  requestedAt?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  isActive?: boolean | null;
  candidateCount?: number | null;
  createdCount?: number | null;
  updatedCount?: number | null;
  deletedCount?: number | null;
  protectedCount?: number | null;
  alarmCount?: number | null;
  pendingRerunProtocolIds?: string | number[] | null;
  errorMessage?: string | null;
  /** Viene de la API pero NUNCA se muestra en la UI. */
  errorStackTrace?: string | null;
}

/** Resultado de pedir una sincronización: la corrida creada o el aviso de que ya hay una activa (409). */
export type ProviderSyncRequestResult =
  | { conflict: false; run: ProviderSyncRun | undefined }
  | { conflict: true; activeRunId: string | undefined };

/** Alarma de una corrida (ProviderSettlementAlarmViewModel). */
export interface ProviderSyncAlarm {
  id: string;
  syncProcessId?: string | null;
  providerId?: string | null;
  protocolId?: number | null;
  sourceProtocolConsumptionId?: string | null;
  patientNumberInProtocol?: string | null;
  medicalRecordNumber?: string | null;
  contractConceptId?: number | null;
  medicalServiceId?: number | null;
  serviceName?: string | null;
  serviceDueDate?: string | null;
  settlementPath?: number | null;
  reason?: number | null;
  reasonDescription?: string | null;
  detail?: string | null;
}

export interface ProviderSyncAlarmsFilter {
  reason?: number;
  protocolId?: number;
  pageNumber: number;
  pageSize: number;
}

export interface ProviderSyncAlarmsPage {
  items: ProviderSyncAlarm[];
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
}

/** Cuerpo de POST providers/{ProviderId:int}/consumptions/revalue-imported (spec 008, MP 030). */
export interface RevalueImportedRequest {
  /** Sólo los importados de ese archivo; ausente = todos los importados del proveedor. */
  providerFileId?: string;
}

/** Resumen de la revalorización de los consumos importados (origen 5). */
export interface RevalueImportedResult {
  total: number;
  /** Valorizados con el valor del archivo (costValuationSource = 4). */
  byFileValue: number;
  /** Valorizados con la lista de precios del proveedor (costValuationSource = 2). */
  byPriceList: number;
  /** Sin valorizar (quedan con settlementAlarmReason). */
  unvalued: number;
}

/** Archivo a "Deshacer importación" (spec 010, US7): lo arma la sección desde el job, el archivo del asistente o el filtro. */
export interface UndoImportTarget {
  fileId: string;
  fileName: string;
  /** Consumos importados si se conocen (job.result.created, o total de la grilla filtrada sólo por el archivo). */
  count?: number;
}
